import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  Bell,
  CheckCircle2,
  Clock,
  RefreshCw,
  XCircle,
  Wrench,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import type { StatusComponentDto, IncidentDto } from '@/schemas';

const STATUS_KEYS = {
  operational: { label: 'Operativo', icon: CheckCircle2, variant: 'success' as const },
  degraded: { label: 'Degradado', icon: AlertTriangle, variant: 'warning' as const },
  partial_outage: { label: 'Degradado', icon: AlertTriangle, variant: 'warning' as const },
  major_outage: { label: 'Caída', icon: XCircle, variant: 'destructive' as const },
  outage: { label: 'Caída', icon: XCircle, variant: 'destructive' as const },
  maintenance: { label: 'Mantenimiento', icon: Wrench, variant: 'secondary' as const },
};

const STATUS_API_BASE_URL =
  (import.meta.env.VITE_STATUS_API_URL as string | undefined) ??
  'https://status-api.cms.express';

async function fetchStatusJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${STATUS_API_BASE_URL}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    throw new Error(`Status API ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export const Route = createFileRoute('/_authed/status')({
  component: StatusPage,
});

function StatusPage() {
  const { t } = useTranslation();
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['status', 'v1', now],
    queryFn: async () => {
      const [componentsRes, incidentsRes] = await Promise.all([
        fetchStatusJson<{ components: StatusComponentDto[] }>('/v1/status/components'),
        fetchStatusJson<{ incidents: IncidentDto[] }>('/v1/status/incidents'),
      ]);
      return { components: componentsRes.components, incidents: incidentsRes.incidents };
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  const components = data?.components ?? [];
  const incidents = data?.incidents ?? [];
  const hasData = components.length > 0;
  const allOperational = hasData
    ? components.every((c) => c.status === 'operational' || c.status === 'maintenance')
    : true;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('status.title')}</h1>
          <p className="text-muted-foreground">
            Estado en tiempo real de los 11 workers y servicios
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <RefreshCw className="h-3 w-3 animate-spin" /> actualizando
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('status.refresh')}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {allOperational ? (
              <CheckCircle2 className="h-6 w-6 text-success" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-warning" />
            )}
            <div>
              <p className="text-lg font-semibold">
                {allOperational
                  ? 'Todos los sistemas operativos'
                  : 'Algunos sistemas tienen incidencias'}
              </p>
              <p className="text-xs text-muted-foreground">
                Última verificación: {formatRelative(now, 'es')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-36" />
            ))
          : components.map((c) => <ComponentCard key={c.id} component={c} />)}
      </div>

      <IncidentsSection incidents={incidents} />
      <SubscribeCard />
    </div>
  );
}

function IncidentsSection({ incidents }: { incidents: IncidentDto[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Incidencias
        </CardTitle>
        <CardDescription>Últimos eventos e incidencias</CardDescription>
      </CardHeader>
      <CardContent>
        {incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin incidencias activas</p>
        ) : (
          <ul className="space-y-4">
            {incidents.map((inc) => (
              <li key={inc.id} className="rounded-md border p-4">
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      inc.status === 'resolved'
                        ? 'success'
                        : inc.status === 'monitoring' || inc.status === 'identified'
                          ? 'warning'
                          : 'destructive'
                    }
                  >
                    {inc.status}
                  </Badge>
                  <Badge variant="outline">{inc.severity}</Badge>
                  <span className="font-medium">{inc.title}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  <Clock className="mr-1 inline h-3 w-3" />
                  Inicio: {formatRelative(inc.startedAt, 'es')}
                  {inc.resolvedAt && (
                    <> · Resuelto: {formatRelative(inc.resolvedAt, 'es')}</>
                  )}
                </p>
                <ul className="mt-3 space-y-1 text-sm">
                  {inc.updates.map((u) => (
                    <li key={u.id} className="rounded bg-muted/50 p-2">
                      <span className="text-muted-foreground">
                        {formatRelative(u.postedAt, 'es')}:
                      </span>{' '}
                      {u.message}
                    </li>
                  ))}
                </ul>
                {inc.postMortem && (
                  <div className="mt-3 rounded border-l-4 border-primary bg-primary/5 p-3 text-sm">
                    <strong>Post-mortem: </strong>
                    <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                      {inc.postMortem}
                    </p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SubscribeCard() {
  const [email, setEmail] = React.useState('');
  const [feedback, setFeedback] = React.useState<{ kind: 'success' | 'error'; message: string } | null>(
    null,
  );
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await fetch(`${STATUS_API_BASE_URL}/v1/status/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = (await res.json()) as {
        success?: boolean;
        subscribed?: boolean;
        reactivated?: boolean;
        alreadySubscribed?: boolean;
      };
      const msg = data.reactivated
        ? 'Suscripción reactivada.'
        : data.alreadySubscribed
          ? 'Ya estabas suscrito.'
          : 'Te hemos añadido a la lista. Recibirás un email cuando haya incidencias.';
      setFeedback({ kind: 'success', message: msg });
      setEmail('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      setFeedback({ kind: 'error', message: `No se pudo suscribir: ${message}` });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Suscribirse a actualizaciones
        </CardTitle>
        <CardDescription>
          Te avisaremos por email cuando se declare o resuelva una incidencia.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label htmlFor="status-subscribe-email" className="sr-only">
              Email
            </Label>
            <Input
              id="status-subscribe-email"
              type="email"
              required
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="email"
            />
          </div>
          <Button type="submit" disabled={submitting || !email}>
            {submitting ? 'Enviando…' : 'Suscribirse'}
          </Button>
        </form>
        {feedback && (
          <p
            className={`mt-2 text-xs ${
              feedback.kind === 'success' ? 'text-success' : 'text-destructive'
            }`}
          >
            {feedback.message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ComponentCard({ component }: { component: StatusComponentDto }) {
  const cfg = STATUS_KEYS[component.status as keyof typeof STATUS_KEYS] ?? STATUS_KEYS.operational;
  const Icon = cfg.icon;
  const { data: sparkData } = useQuery({
    queryKey: ['status', 'uptime', component.id],
    queryFn: async () => {
      const now = Math.floor(Date.now() / 1000);
      const from = now - 24 * 60 * 60;
      const res = await fetchStatusJson<{
        series: Array<{ checkedAt: number; latencyMs: number | null; status: string }>;
      }>(`/v1/status/uptime?component=${encodeURIComponent(component.id)}&from=${from}&to=${now}`);
      return res.series.map((row) => ({
        h: row.checkedAt,
        v: row.latencyMs ?? component.p95Ms ?? 0,
      }));
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const spark = sparkData && sparkData.length > 0
    ? sparkData
    : Array.from({ length: 24 }, (_, i) => ({
        h: i,
        v: component.p95Ms ?? 50,
      }));
  const uptime30d = component.uptime30d ?? 100;
  const uptime90d = component.uptime90d ?? 100;
  const uptime30dColor = uptimeColor(uptime30d);
  const uptime90dColor = uptimeColor(uptime90d);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{component.name}</CardTitle>
            <CardDescription>{component.description}</CardDescription>
          </div>
          <Badge variant={cfg.variant} className="gap-1">
            <Icon className="h-3 w-3" /> {cfg.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-12">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark}>
              <defs>
                <linearGradient id={`g-${component.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(199 89% 48%)" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="hsl(199 89% 48%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis hide />
              <XAxis hide dataKey="h" />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.5rem',
                }}
                cursor={{ stroke: 'hsl(var(--border))' }}
                formatter={(v: number) => `${Math.round(v)}ms`}
              />
              <Area
                type="monotone"
                dataKey="v"
                stroke="hsl(199 89% 48%)"
                fill={`url(#g-${component.id})`}
                strokeWidth={1.5}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1 text-center text-xs">
          <div>
            <p className={`font-semibold ${uptime30dColor}`}>{uptime30d.toFixed(2)}%</p>
            <p className="text-muted-foreground">30d</p>
          </div>
          <div>
            <p className={`font-semibold ${uptime90dColor}`}>{uptime90d.toFixed(2)}%</p>
            <p className="text-muted-foreground">90d</p>
          </div>
          <div>
            <p className="font-semibold">{component.p95Ms ?? 0}ms</p>
            <p className="text-muted-foreground">p95</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function uptimeColor(value: number): string {
  if (value > 99.9) return 'text-success';
  if (value > 99) return 'text-warning';
  return 'text-destructive';
}

// Re-export api for callers that want to subscribe from other places.
export { api };
